// Profit / loss of a trade for the fees modal, docs/trade-logic.md 6.4

/**
 * computePL from docs/trade-logic.md 6.4: the loss is capped at the wager.
 */
export const computePL = (
  openPrice,
  closePrice,
  amount,
  leverage,
  kind,
  rollingFee = 0,
) => {
  if (!closePrice) return 0;

  const direction = kind === "short" ? -1 : 1;
  const pl =
    ((closePrice - openPrice) / openPrice) * amount * leverage * direction -
    rollingFee;

  return Math.max(pl, -amount);
};

/**
 * Close price after market impact; only a winning trade is affected.
 *
 * @param {object} params
 * @param {object|null} params.riskSettings asset's marketImpact* settings
 */
export const getFilledClosePrice = ({
  openPrice,
  rawClosePrice,
  amount,
  leverage,
  kind,
  riskSettings,
}) => {
  const direction = kind === "short" ? -1 : 1;
  const isWinning = (rawClosePrice - openPrice) * direction > 0;

  // Assets without riskSettings (not in the mock yet) aren't adjusted.
  if (!isWinning || !riskSettings) return rawClosePrice;

  const {
    marketImpactBaseRate: baseRate,
    marketImpactLiquidityUnit: liquidityUnit,
    marketImpactMinMove: minMove,
    marketImpactMinPayoutFactor: minPayoutFactor,
    marketImpactMoveExponent: moveExponent,
    marketImpactReferenceMove: referenceMove,
    marketImpactSizeExponent: sizeExponent,
  } = riskSettings;
  const notional = amount * leverage;
  const f =
    (notional / liquidityUnit) ** sizeExponent * referenceMove ** moveExponent;
  const move = Math.max(Math.abs(rawClosePrice / openPrice - 1), minMove);
  const toxicity = f / move ** moveExponent;
  const payoutFactor = Math.max(
    minPayoutFactor,
    (1 - baseRate) / (1 + toxicity),
  );

  return openPrice + (rawClosePrice - openPrice) * payoutFactor;
};

/**
 * ROI tab result for a price move in percent.
 *
 * @returns {{ pl: number, multiplier: number }}
 */
export const getRoiResult = ({
  wager,
  leverage,
  priceMovePercent,
  kind = "long",
  riskSettings = null,
  maxProfit = null,
}) => {
  if (!wager) return { pl: 0, multiplier: 1 };

  // Only the relative move matters, so any open price works
  const openPrice = 1;
  const rawClosePrice = openPrice * (1 + priceMovePercent / 100);
  const filledClosePrice = getFilledClosePrice({
    openPrice,
    rawClosePrice,
    amount: wager,
    leverage,
    kind,
    riskSettings,
  });
  let pl = computePL(openPrice, filledClosePrice, wager, leverage, kind);

  if (maxProfit != null) pl = Math.min(pl, maxProfit);

  return { pl, multiplier: pl / wager + 1 };
};
